import React, { useEffect, useRef, useState } from 'react';
import styles from './click-calendar-card.module.scss';
import { ICalendarBookingType } from 'shared/api/booking/interfaces/calendar-booking-type.interface';
import { BookingApi } from 'shared/api';
import { DefaultButton, formatPhoneNumber } from 'shared/ui';
import { MonthNamesNum } from 'shared/consts/calendar.consts';
import { CalendarCardType } from '../types/business-card.type';
import { ICalendarBreakType } from 'shared/api/booking/interfaces/calendar-break-type.interface';
import { QuickBooking } from './quick-booking/quick-booking';
import { BreakCreate } from './break-create/break-create';
import { dayCalendarStore } from 'entities/calendar/model/day-calendar.store';
import { observer } from 'mobx-react-lite';
import { parseDateFromDatetime } from 'shared/lib';

interface ClickCalendarCardProps {
  id: string;
  employeeId: string;
  onClose?: (updateData?: boolean) => void;
  cardType: CalendarCardType;
}

export const ClickCalendarCard: React.FC<ClickCalendarCardProps> = observer(
  ({ id, employeeId, onClose, cardType }) => {
    const cardRef = useRef<HTMLDivElement>(null);
    const onCloseRef = useRef(onClose);
    useEffect(() => {
      onCloseRef.current = onClose;
    }, [onClose]);
    const [bookingInfo, setBookingInfo] = useState<ICalendarBookingType>();
    const [editingModal, setEditingModal] = useState<
      | {
          type: 'booking';
          data: ICalendarBookingType;
        }
      | {
          type: 'break';
          data: ICalendarBreakType;
        }
      | null
    >(null);
    const deleteBooking = (bookingId: string) => {
      BookingApi.deleteBooking({ bookingId }).then(() => {
        onCloseRef.current?.(true);
      });
    };
    const [breakInfo, setBreakInfo] = useState<ICalendarBreakType>();

    const handleEditBooking = async () => {
      const data = await BookingApi.getCalendarBooking({ bookingId: id });
      setEditingModal({ type: 'booking', data });
    };

    const handleEditBreak = async () => {
      const data = await BookingApi.getCalendarBreak({ breakId: id });
      setEditingModal({ type: 'break', data });
    };

    const handleDeleteBreak = () => {
      if (!breakInfo) return;
      if (!dayCalendarStore.getSelectedDate) return;
      BookingApi.deleteBreakOverride(
        id,
        dayCalendarStore.getSelectedDateAsDate
      ).then(() => {
        onCloseRef.current?.(true);
      });
    };

    useEffect(() => {
      if (cardType == 'booking') {
        BookingApi.getCalendarBooking({ bookingId: id })
          .then((data) => {
            setBookingInfo(data);
          })
          .catch(() => {
            onCloseRef.current?.();
          });
      } else if (cardType == 'break') {
        BookingApi.getCalendarBreak({ breakId: id })
          .then((data) => {
            setBreakInfo(data);
          })
          .catch(() => {
            onCloseRef.current?.();
          });
      }
    }, [id, cardType]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (editingModal) return;
        if (
          cardRef.current &&
          !cardRef.current.contains(event.target as Node)
        ) {
          onCloseRef.current?.();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }, [editingModal]);
    const getComponent = () => {
      switch (cardType) {
        case 'booking':
          return bookingComponent;
        case 'break':
          return breakComponent;
        default:
          return <div></div>;
      }
    };

    const bookingComponent = (
      <div className={styles.booking__container__info}>
        <div className={styles.container__info__title}>Информация о записи</div>
        <div className={styles.info__client_fio}>
          <span className={styles.client_fio__title}>Клиент: </span>{' '}
          <span className={styles.client_fio}>
            {bookingInfo?.customer.name || 'Имя не указано'}
          </span>
        </div>
        <div className={styles.info__client_telephone}>
          <span className={styles.client_telephone__title}>Телефон:</span>{' '}
          <span className={styles.client_telephone}>
            {bookingInfo?.customer.phone
              ? formatPhoneNumber(bookingInfo?.customer.phone)
              : 'Не указан'}
          </span>
        </div>
        <div className={styles.info__booking_date}>
          <span className={styles.booking_date__title}>Дата и время:</span>{' '}
          <span className={styles.booking_date}>
            {' '}
            {bookingInfo?.startDate.split('-')[2]}{' '}
            {bookingInfo?.startDate
              ? MonthNamesNum[
                  parseInt(bookingInfo?.startDate.split('-')[1]) - 1
                ]
              : ''}
            {', '}
          </span>
          <span className={styles.booking_time}>
            {bookingInfo?.time.startedAt} - {bookingInfo?.time.finishedAt}
          </span>
        </div>
        <div className={styles.info__booking_service}>
          <span className={styles.booking_service__title}>Услуга:</span>{' '}
          <span className={styles.booking_service}>
            {bookingInfo?.serviceName}
          </span>
        </div>
        <div className={styles.info__booking_price}>
          <span className={styles.booking_price__title}>Цена: </span>{' '}
          <span className={styles.booking_price}>
            {bookingInfo?.price} &#8381;
          </span>
        </div>
        <div className={styles.info__booking_comment}>
          <span className={styles.booking_comment__title}>Комментарий: </span>{' '}
          {bookingInfo?.comment || 'Комментарий отсутствует'}
        </div>
        <div className={styles.container__info__buttons}>
          <div className={styles.button__cancel}>
            <DefaultButton
              buttonText="Отменить запись"
              color="red"
              className={styles.button_card_action}
              onClick={() => deleteBooking(id)}
            />
          </div>
          <div>
            {' '}
            <DefaultButton
              buttonText="Редактировать"
              color="green"
              className={styles.button_card_action}
              onClick={handleEditBooking}
            />
          </div>
        </div>
      </div>
    );
    const breakComponent = (
      <div className={styles.break__container__info}>
        <div className={styles.container__info__title}>
          Информация о перерыве
        </div>
        <div className={styles.info__break_name}>
          <span className={styles.break_name__title}>Название: </span>{' '}
          <span className={styles.break_name}>{breakInfo?.title}</span>
        </div>
        <div className={styles.info__break_time}>
          <span className={styles.break_time__title}>Время: </span>{' '}
          <span className={styles.break_time}>
            {breakInfo?.timeRule.startedAt} - {breakInfo?.timeRule.finishedAt}
          </span>
        </div>
        <div className={styles.info__break_rule}>
          <span className={styles.break_time__rule}>Правило перерыва: </span>{' '}
          <span className={styles.break_rule}>
            {breakInfo?.daysRule.countDaysRunning} через{' '}
            {breakInfo?.daysRule.countDaysOver} c{' '}
            {parseDateFromDatetime(breakInfo?.dateStart || '')}
          </span>
        </div>

        <div className={styles.container__info__buttons}>
          <div className={styles.button__cancel}>
            <DefaultButton
              buttonText="Удалить перерыв"
              color="red"
              className={styles.button_card_action}
              onClick={handleDeleteBreak}
            />
          </div>
          <div>
            {' '}
            <DefaultButton
              buttonText="Редактировать"
              color="green"
              className={styles.button_card_action}
              onClick={handleEditBreak}
            />
          </div>
        </div>
      </div>
    );
    return (
      <div ref={cardRef} className={styles.booking_card__container}>
        <button className={styles.close_button} onClick={() => onClose?.()}>
          ×
        </button>
        {getComponent()}
        {editingModal?.type === 'booking' && editingModal.data && (
          <div onClick={(e) => e.stopPropagation()}>
            <QuickBooking
              isEdit
              bookingData={editingModal.data}
              startTime={editingModal.data.time.startedAt}
              employeeId={employeeId}
              date={new Date(editingModal.data.startDate)}
              onClose={() => setEditingModal(null)}
              onCreated={() => {
                setEditingModal(null);
                BookingApi.getCalendarBooking({ bookingId: id }).then(
                  setBookingInfo
                );
              }}
            />
          </div>
        )}
        {editingModal?.type === 'break' && editingModal.data && (
          <div onClick={(e) => e.stopPropagation()}>
            <BreakCreate
              isEdit
              breakData={editingModal.data}
              startTime={editingModal.data.timeRule.startedAt}
              employeeId=""
              date={new Date(editingModal.data.dateStart)}
              onClose={() => setEditingModal(null)}
              onCreated={() => {
                setEditingModal(null);
                BookingApi.getCalendarBreak({ breakId: id }).then(setBreakInfo);
              }}
            />
          </div>
        )}
      </div>
    );
  }
);
